/**
 * Who may open the CPA rule-management surface.
 *
 * Follows the same shape as `ivt/utils/authorization.ts`: this surface carries its
 * OWN gate on its OWN groups and is mounted OUTSIDE the main `<AccessGate>`, because
 * the audience is not the main-app audience. The frontend gate is UX only — the
 * authoritative check is server-side.
 *
 * ## The group situation today, stated plainly
 *
 * The live CPA backend (`SalesRecommendationHub`) knows exactly TWO levels, both read
 * off the Federate id-token's `ldap_groups` by `FederateIdTokenValidator` and enforced
 * in `AuthContext.requireDealDeskCaller`:
 *
 *   - `admin`          — blanket admin for the Deal Desk Tool
 *   - `deal_desk_tool` — may use the questionnaire (the BD audience)
 *
 * That is a USE permission, not an EDIT permission. Nothing in the live service
 * distinguishes "may answer the questionnaire" from "may change the rules that decide
 * what the questionnaire recommends" — because until now the rules were a JSON file in
 * a Lambda and the only way to change them was a code review plus a deploy. The moment
 * this surface can write rules, that distinction has to exist, and it has to exist
 * SERVER-SIDE first.
 *
 * So the three roles the surface needs are not all available yet:
 *
 * | role     | what it does                        | group                                    |
 * |----------|-------------------------------------|------------------------------------------|
 * | viewer   | read the tree, rules, matrix        | `deal_desk_tool` (existing BD group)     |
 * | editor   | change the draft                    | ⚠ NOT CREATED YET — see CPA_EDITOR_GROUP |
 * | approver | publish a draft to live             | `rocky-admin` for now                    |
 *
 * `CPA_EDITOR_GROUP` is therefore a placeholder that intentionally does NOT resolve to
 * a real group: nothing grants it, so `canEditRules` is false for everyone except
 * platform admins until the group is created and the service gates on it. Writing a
 * plausible-looking group name here and shipping it would produce a surface that looks
 * permission-controlled and is not.
 */

/** Rocky platform admin. Also the prototype's 审批者 (publish rights). */
export const ROCKY_ADMIN_GROUP = 'rocky-admin';

/**
 * The existing Deal Desk Tool audience, as the CPA service spells it. Admits a user
 * to READ this surface — a BD who wants to understand why they got a recommendation
 * should be able to look at the rules.
 */
export const DEAL_DESK_TOOL_GROUP = 'deal_desk_tool';

/** The CPA service's own blanket admin group (note: `admin`, not `rocky-admin`). */
export const DEAL_DESK_ADMIN_GROUP = 'admin';

/**
 * ⚠ PLACEHOLDER — no such LDAP group exists.
 *
 * Kept as a named constant rather than a TODO comment so that (a) the gate reads the
 * way it will read once the group is real, and (b) grepping for this name finds every
 * place that has to change. Until the group is created AND
 * `SalesRecommendationHub` gates rule-writing operations on it, only
 * {@link ROCKY_ADMIN_GROUP} can edit.
 */
export const CPA_EDITOR_GROUP = 'TODO-cpa-rule-editor';

/** Any of these opens the surface at all (read-only is a valid state here). */
export const CPA_ACCESS_GROUPS: readonly string[] = [
  DEAL_DESK_TOOL_GROUP,
  DEAL_DESK_ADMIN_GROUP,
  ROCKY_ADMIN_GROUP,
];

export function hasCpaAccess(ldapGroups: readonly string[] | undefined): boolean {
  return (ldapGroups ?? []).some((g) => CPA_ACCESS_GROUPS.includes(g));
}

/**
 * May change the draft.
 *
 * Deliberately narrow. A rule change here moves real money — it decides which
 * investment programs a seller is told to go after — so the default is read-only and
 * the editor role is opt-in, not inherited from "can use the tool".
 */
export function canEditRules(ldapGroups: readonly string[] | undefined): boolean {
  const g = ldapGroups ?? [];
  return g.includes(ROCKY_ADMIN_GROUP) || g.includes(CPA_EDITOR_GROUP);
}

/**
 * May publish a draft so BDs see it.
 *
 * Same group as editing today, which means the prototype's two-step
 * "编辑者提交审批 → 平台管理员批准并发布" is NOT enforced yet: one person can both
 * edit and publish. Separating them needs the editor group above plus an approval
 * record in the service; this function is where that split lands.
 */
export function canPublishRules(ldapGroups: readonly string[] | undefined): boolean {
  return (ldapGroups ?? []).includes(ROCKY_ADMIN_GROUP);
}

/** What the surface should tell a user who got in but cannot change anything. */
export function readOnlyReason(ldapGroups: readonly string[] | undefined): string | null {
  if (canEditRules(ldapGroups)) return null;
  return `当前为只读：修改 CPA 规则需要 ${ROCKY_ADMIN_GROUP} 权限（独立的规则编辑组尚未创建）。`;
}
